import React from 'react'
import {
  FileCheck,
  UserCheck,
  Scale,
  Calculator,
  User,
  Eye,
  Sparkles,
  Briefcase,
  Building2,
} from 'lucide-react'
import type { UserType } from '../../types/exchange'
import { roleLabel } from '../../lib/roleFormatting'

export type ExtendedUserType = UserType | 'custom'

export interface UserTypeOption {
  type: ExtendedUserType
  icon: React.ElementType
  description: string
}

export const USER_TYPE_OPTIONS: UserTypeOption[] = [
  {
    type: 'exchanger',
    icon: User,
    description: 'Property owner doing a 1031 exchange — full access.',
  },
  {
    type: 'qi_officer',
    icon: FileCheck,
    description: 'QI staff managing the exchange — process without admin.',
  },
  {
    type: 'advisor',
    icon: UserCheck,
    description: 'Real estate advisor or broker — read access plus marketplace.',
  },
  {
    type: 'attorney',
    icon: Scale,
    description: 'Legal counsel — document and compliance review.',
  },
  {
    type: 'accountant',
    icon: Calculator,
    description: 'CPA or tax professional — financial data and IRS forms.',
  },
  {
    type: 'title_company',
    icon: Building2,
    description: 'Title or escrow officer — document and closing access.',
  },
  {
    type: 'lender',
    icon: Briefcase,
    description: 'Bank or mortgage representative — read-only financial access.',
  },
  {
    type: 'viewer',
    icon: Eye,
    description: 'Read-only access to documents and forms.',
  },
  {
    type: 'custom',
    icon: Sparkles,
    description: 'Custom per-domain permission configuration.',
  },
]

interface UserTypeSelectorProps {
  value: ExtendedUserType
  onChange: (type: ExtendedUserType) => void
  disabled?: boolean
}

export const UserTypeSelector: React.FC<UserTypeSelectorProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {USER_TYPE_OPTIONS.map(({ type, icon: Icon, description }) => {
        const selected = value === type
        return (
          <button
            key={type}
            type="button"
            onClick={() => !disabled && onChange(type)}
            aria-pressed={selected}
            disabled={disabled}
            className={[
              'flex flex-col items-start p-4 rounded-xl border-2 text-left transition-all',
              selected
                ? 'border-primary bg-primary/5 ring-1 ring-primary'
                : 'border-border bg-card hover:border-primary/40',
              disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
            ].join(' ')}
          >
            <div className="flex items-center gap-3 mb-2">
              <Icon
                className={[
                  'h-5 w-5 flex-shrink-0',
                  selected ? 'text-primary' : 'text-muted-foreground',
                ].join(' ')}
              />
              <span
                className={[
                  'font-semibold text-sm',
                  selected ? 'text-primary' : 'text-foreground',
                ].join(' ')}
              >
                {roleLabel(type)}
              </span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
          </button>
        )
      })}
    </div>
  )
}
